import { ScrollAxis, ScrollPane } from "../../controls/ScrollPane";
import { ScrollPaneContent } from "../../controls/scrollpane/ScrollPaneContent";
import { SmartMask } from "../../mask/SmartMask";
import { Padding } from "../../utils/Padding";
import { Showable } from "../../mixins/Showable";
import { ApplyScalingBias } from "../../utils/ApplyScalingBias";
import { ScrollPaneTitledListContent } from "./dealcomplete/ScrollPaneTitledListContent";


export type ContentScrollData = {
    title?: string;
    capitalizedTitle?: string;
    scrollY?: number;
}

// get ts linting
export interface ContentsScrollPane extends Showable { }

/**
 * ScrollPane with multiple tagged 'sub-content' support
 * - can jump to specific subcontent
 */
export class ContentsScrollPane
    extends ScrollPane {
    protected contents: ScrollPaneTitledListContent[];
    protected contentSpacing: Padding = new Padding(0, 0, 0, 60);
    /**
     * Maximum width and thus referencial width to which content will scale itself
     */
    protected referenceWidth: number;

    protected contentsScrollData: ContentScrollData[];
    /**
     * Currently focused content
     * NOTE: this can change by clicking arrow at the top left/right of header/title, or via scrolling - which does calculate closest current content and changes the title.
     * NOTE: During transition: Manual scrub updates TITLE, but Arrow-Click scroll DOES NOT (only updates on click).
     * NOTE: expects no gaps in contentsScrollData/contents array
     * NOTE: set to 0, so that title in parent can be set to first category's title. The actual position of such content will use it's default position scrollY=0 (from Init), since the position update happens in SetSize ().
     */
    public curContentIndex: number = 0;
    /**
     * On this level of multiple contents, @see OnScrollAmountChange needs to know whether 
     * scroll position updated by setting @see curContentIndex first,
     * or by simply setting scrollAmount (so then @see curScrollIndex still needs to be additionally updated)
     */
    public fromSetIndexToScrollUpdate: boolean = false;


    public Init(cullItems: boolean = true, referenceWidth: number = undefined): void {
        // console.log("Init");

        super.Init(cullItems);

        this.referenceWidth = referenceWidth;

        // defaults (override in subclass or externally)
        this.scrollAxis = ScrollAxis.VERTICAL;
        this.excessScrollAmount = new Padding(0);
        // this.excessScrollAmount = new Padding(0, 0, 5, 150);

        this.contentsScrollData = [];
        this.contents = [];
    }

    public Build(): void {
        // > build overlay
        super.Build();

        // content container
        this.content = new ScrollPaneContent(this.scene, 0, this.excessScrollAmount.top);
        this.content.Init();
        this.add(this.content);

        // mask
        this.contentMask = new SmartMask();
        // this.contentMask.DEBUG = true;
        this.contentMask.Init(this, new Phaser.Geom.Rectangle(), new Padding(10, 0));
        this.contentMask.SetContent(this.content);
        // this.contentMask.SetPosition(200, 200);

        // add overlay
        this.add(this.overlay);
    }

    public GetReferenceWidth(): number | undefined {
        return this.referenceWidth;
    }

    public GetContents(): ScrollPaneTitledListContent[] {
        return this.contents;
    }

    /**
     * Returns number of contentes
     */
    public GetContentsLength(): number {
        return this.contents.length;
    }

    /**
     * NOTE: this is called from parent (not automatically by this class)
     */
    public UpdateFromLocalizationChange(): void {
        // override in subclass
    }

    public GetContent(contentIndex: number): ScrollPaneTitledListContent {
        return this.contents[contentIndex];
    }

    public GetContentScrollData(contentId: number): ContentScrollData {
        return this.contentsScrollData[contentId];
    }

    public GetCurrentContentScrollData(): ContentScrollData {
        return this.contentsScrollData[this.curContentIndex];
    }

    public ScrollToPrevContent(): void {
        if (--this.curContentIndex < 0) {
            this.curContentIndex = this.contentsScrollData.length - 1;
        }
        const scrollY: number = this.contentsScrollData[this.curContentIndex].scrollY;
        this.curScrollAmount.set(0, scrollY);
        // curContnetIndex already updated. 
        // Do not again update it from change in scroll amount @seeOnScrollAmountChange
        this.fromSetIndexToScrollUpdate = true;
        this.SetScroll(this.curScrollAmount, false, true);
    }

    public ScrollToNextContent(): void {
        if (++this.curContentIndex > this.contentsScrollData.length - 1) {
            this.curContentIndex = 0;
        }
        const scrollY: number = this.contentsScrollData[this.curContentIndex].scrollY;
        // console.log(scrollY)
        this.curScrollAmount.set(0, scrollY);
        // curContnetIndex already updated. 
        // Do not again update it from change in scroll amount @seeOnScrollAmountChange
        this.fromSetIndexToScrollUpdate = true;

        this.SetScroll(this.curScrollAmount, false, true);
    }

    protected override PreOnGameObjectPointerWheel() {
        // update of scroll position not as result of prior curContentIndex change
        this.fromSetIndexToScrollUpdate = false;
    }
    
    protected override PreOnScrollDrag(): void {
        // update of scroll position not as result of prior curContentIndex change
        this.fromSetIndexToScrollUpdate = false;
    }

    protected override OnScrollAmountChange(): void {
        // if this change is not result of prior curContentIndex change.
        if (!this.fromSetIndexToScrollUpdate) {
            // change index from content pos
            this.curContentIndex = this.GetContentIndexByScrollAmount(this.curScrollAmount);
            this.fromSetIndexToScrollUpdate = false;
        }
    }

    protected GetContentIndexByScrollAmount(scrollAmount: Phaser.Math.Vector2): number {
        let i: number = 0;
        for (i = 0; i < this.contentsScrollData.length; i++) {
            const curScrollData = this.contentsScrollData[i];
            // console.log(this.curScrollAmount.y, curScrollData.scrollY);
            if (this.curScrollAmount.y > curScrollData.scrollY) {
                return i - 1;
            }
        }
        return this.contentsScrollData.length - 1;
    }

    /**
     * Set width and height of area available to be occupied by this container
     * - updates from width (reduced) the size of content
     * - updates from width and height (reduced) the size of mask
     * @param width 
     * @param height 
     */
    public override SetSize(width: number, height: number): void {
        // console.trace("SetSize")
        super.SetSize(width, height);

        const referenceWidth = this.referenceWidth == undefined ? width : this.referenceWidth;
        const assetScale: number = ApplyScalingBias(width / referenceWidth, 1);

        // recalc size of all contents
        this.contents.forEach((content: ScrollPaneTitledListContent) => {
            content.scale = assetScale;
            content.SetWidth(referenceWidth);
        });

        // by now all contents are resized properly and their GetSize is correct.
        // stack them on top of eachother
        // and set new total size
        let totalHeight: number = 0;
        this.contents.forEach((content: ScrollPaneTitledListContent, i) => {
            content.y = totalHeight;
            this.contentsScrollData[i].scrollY = -totalHeight + this.excessScrollAmount.top;
            totalHeight += (content.GetSize().height + this.contentSpacing.bottom) * assetScale;
        });
        totalHeight -= this.contentSpacing.bottom * assetScale;
        this.content.SetSize(width, totalHeight);
        this.contentMask.SetMaskRectSize(width, height);
        // update transforms as well, since this container's position might have changed also
        this.contentMask.UpdateTransforms();

        // update scroll to keep content within bounds
        this.UpdateInPlace(true);
    }
}